import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './AdminDashboard.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function Stat({ label, value, sub }) {
  return (
    <div className="adash__stat">
      <div className="adash__stat-label">{label}</div>
      <div className="adash__stat-value">{value}</div>
      {sub != null && <div className="adash__stat-sub">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [token, setToken] = useState(sessionStorage.getItem('admin_token') || '');
  const [stats, setStats]     = useState(null);
  const [queue, setQueue]     = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);

  async function fetchStats() {
    if (!token) return;
    setLoading(true);
    try {
      const [statsRes, queueRes] = await Promise.all([
        fetch(`${API}/admin/stats`,  { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/queue`,  { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!statsRes.ok) throw new Error((await statsRes.json()).error || 'Could not load stats');
      setStats(await statsRes.json());
      if (queueRes.ok) setQueue(await queueRes.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function clearQueue(game) {
    const url = game ? `${API}/admin/queue?game=${game}` : `${API}/admin/queue`;
    await fetch(url, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchStats();
  }

  useEffect(() => { fetchStats(); }, [token]);
  useEffect(() => {
    if (!token) return;
    const id = setInterval(fetchStats, 15_000);
    return () => clearInterval(id);
  }, [token]);

  if (!token) {
    return (
      <div className="adash">
        <div className="adash__unauth">
          <h2>Admin access required</h2>
          <p>Go to <Link to="/admin">/admin</Link> and log in first. The token will be saved to this browser.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="adash">
      <Helmet>
        <title>Launch Dashboard · Admin | EZGAME</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="adash__header">
        <h1 className="adash__title">🚀 Launch Dashboard</h1>
        <button className="adash__refresh" onClick={fetchStats} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="adash__error">{error}</div>}

      {stats && (
        <>
          <div className="adash__grid">
            <Stat label="Waitlist — Total"  value={stats.waitlist.total.toLocaleString()} sub={`+${stats.waitlist.today} today`} />
            <Stat label="Users — Total"     value={stats.users.total.toLocaleString()} />
            <Stat label="Entries — Total"   value={stats.entries.total.toLocaleString()} sub={`+${stats.entries.today} today`} />
            <Stat label="Total Paid Out"    value={stats.payoutsUSDC.pretty} sub="90% of resolved pools" />
          </div>

          <div className="adash__grid adash__grid--narrow">
            <Stat label="Tournaments Filling"  value={stats.tournaments.filling  || 0} />
            <Stat label="Tournaments Active"   value={stats.tournaments.active   || 0} />
            <Stat label="Tournaments Resolved" value={stats.tournaments.resolved || 0} />
            <Stat label="Tournaments Failed"   value={stats.tournaments.failed   || 0} />
          </div>

          <h2 className="adash__section-title">Preferred Game (Waitlist)</h2>
          <div className="adash__bars">
            {stats.waitlist.gamePreferences.length === 0
              ? <div className="adash__empty">No preferences recorded yet.</div>
              : stats.waitlist.gamePreferences.map(p => {
                  const max = Math.max(...stats.waitlist.gamePreferences.map(x => x.c));
                  const pct = max ? (p.c / max) * 100 : 0;
                  return (
                    <div key={p.preferred_game} className="adash__bar-row">
                      <div className="adash__bar-label">{p.preferred_game}</div>
                      <div className="adash__bar-track"><div className="adash__bar-fill" style={{ width: `${pct}%` }} /></div>
                      <div className="adash__bar-count">{p.c}</div>
                    </div>
                  );
                })
            }
          </div>

          <h2 className="adash__section-title">Latest Waitlist Signups</h2>
          <div className="adash__table-wrap">
            <table className="adash__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Game</th>
                  <th>Referrer</th>
                  <th>Signed Up</th>
                </tr>
              </thead>
              <tbody>
                {stats.waitlist.recent.map(r => (
                  <tr key={r.id}>
                    <td>#{r.id}</td>
                    <td className="adash__email">{r.email}</td>
                    <td>{r.epic_player_id || r.apex_player_id || '—'}</td>
                    <td>{r.preferred_game || '—'}</td>
                    <td>{r.referrer || '—'}</td>
                    <td>{new Date(r.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {queue && (
            <>
              <h2 className="adash__section-title">
                🎮 Tournament Request Queue ({queue.total})
              </h2>
              <div className="adash__table-wrap">
                {queue.total === 0 ? (
                  <div style={{ padding: 20, color: '#ffffff88', fontSize: 13 }}>No requests yet.</div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid #ffffff11' }}>
                      <button
                        className="adash__refresh"
                        style={{ fontSize: 12 }}
                        onClick={() => clearQueue('fortnite')}
                      >
                        Clear Fortnite Queue
                      </button>
                      <button
                        className="adash__refresh"
                        style={{ fontSize: 12 }}
                        onClick={() => clearQueue('apex')}
                      >
                        Clear Apex Queue
                      </button>
                      <button
                        className="adash__refresh"
                        style={{ fontSize: 12, borderColor: '#FF006E', color: '#FF006E' }}
                        onClick={() => clearQueue()}
                      >
                        Clear All
                      </button>
                    </div>
                    <table className="adash__table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Player</th>
                          <th>Game</th>
                          <th>Message</th>
                          <th>Requested</th>
                        </tr>
                      </thead>
                      <tbody>
                        {queue.queue.map((r, i) => (
                          <tr key={r.id}>
                            <td>{i + 1}</td>
                            <td style={{ fontWeight: 700 }}>{r.handle}</td>
                            <td style={{ textTransform: 'capitalize' }}>{r.game}</td>
                            <td style={{ color: '#ffffff88', fontSize: 12 }}>{r.message || '—'}</td>
                            <td>{new Date(r.created_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </>
          )}

          <p className="adash__footnote">Auto-refreshing every 15s · Generated {new Date(stats.generatedAt * 1000).toLocaleTimeString()}</p>
        </>
      )}
    </div>
  );
}
